export interface CurrencyConfig {
  code: string;
  symbol: string;
  conversionRate: number; // Rate from EUR base
}

export const CURRENCY_MAP: Record<string, CurrencyConfig> = {
  sk: { code: 'EUR', symbol: '€', conversionRate: 1 },
  cs: { code: 'CZK', symbol: 'Kč', conversionRate: 25 },
  da: { code: 'DKK', symbol: 'kr', conversionRate: 7.5 },
  en: { code: 'EUR', symbol: '€', conversionRate: 1 },
  de: { code: 'EUR', symbol: '€', conversionRate: 1 },
};

export function getCurrencyConfig(language: string): CurrencyConfig {
  return CURRENCY_MAP[language] || CURRENCY_MAP.sk;
}

export function formatCurrency(amount: number, language: string): string {
  const config = getCurrencyConfig(language);
  const roundedAmount = Math.round(amount);
  
  // Format based on currency
  if (config.code === 'CZK') {
    return `${roundedAmount.toLocaleString('cs-CZ')} ${config.symbol}`;
  } else if (config.code === 'DKK') {
    return `${roundedAmount.toLocaleString('da-DK')} ${config.symbol}`;
  } else if (language === 'de') {
    return `${roundedAmount.toLocaleString('de-DE')} ${config.symbol}`;
  } else {
    return `${config.symbol}${roundedAmount.toLocaleString('sk-SK')}`;
  }
}

export function convertPrice(euroPrice: number, language: string): number {
  const config = getCurrencyConfig(language);
  return Math.round(euroPrice * config.conversionRate);
}

export function getCurrencySymbol(language: string): string {
  const config = getCurrencyConfig(language);
  return config.symbol;
}

// Subscription plan prices live in src/config/pricing.ts (live billing widget).

const DEFAULT_HOURLY_RATE_BY_CODE: Record<string, number> = {
  EUR: 25,
  CZK: 400,
  DKK: 500,
};

const MAX_HOURLY_RATE_BY_CODE: Record<string, number> = {
  EUR: 200,
  CZK: 2000,
  DKK: 2000,
};

export function getDefaultHourlyRate(language: string): number {
  const config = getCurrencyConfig(language);
  return DEFAULT_HOURLY_RATE_BY_CODE[config.code] ?? 25;
}

export function getMaxHourlyRate(language: string): number {
  const config = getCurrencyConfig(language);
  return MAX_HOURLY_RATE_BY_CODE[config.code] ?? 200;
}

// Realistic per-trade hourly rates (gross billing rate seen by the client),
// calibrated for the SK / CZ / DK construction markets in 2025-2026.
// EUR rates are used for SK and EN.
type TradeRates = { EUR: number; CZK: number; DKK: number };

const PROFESSION_HOURLY_RATES: Record<string, TradeRates> = {
  painter:         { EUR: 18, CZK: 350, DKK: 400 },
  electrician:     { EUR: 30, CZK: 550, DKK: 650 },
  plumber:         { EUR: 28, CZK: 500, DKK: 600 },
  carpenter:       { EUR: 25, CZK: 450, DKK: 550 },
  mason:           { EUR: 22, CZK: 400, DKK: 500 },
  roofer:          { EUR: 26, CZK: 480, DKK: 600 },
  tiler:           { EUR: 22, CZK: 420, DKK: 500 },
  heatingTech:     { EUR: 32, CZK: 580, DKK: 700 },
  hvacTech:        { EUR: 32, CZK: 580, DKK: 700 },
  drywall:         { EUR: 20, CZK: 380, DKK: 450 },
  insulation:      { EUR: 20, CZK: 380, DKK: 450 },
  flooring:        { EUR: 22, CZK: 400, DKK: 500 },
  windowInstaller: { EUR: 24, CZK: 440, DKK: 550 },
  doorInstaller:   { EUR: 24, CZK: 440, DKK: 550 },
  concrete:        { EUR: 22, CZK: 400, DKK: 500 },
  excavation:      { EUR: 30, CZK: 550, DKK: 650 },
  demolition:      { EUR: 20, CZK: 380, DKK: 450 },
  fencing:         { EUR: 20, CZK: 380, DKK: 450 },
  landscaping:     { EUR: 18, CZK: 350, DKK: 400 },
  pools:           { EUR: 28, CZK: 500, DKK: 600 },
};

export function getDefaultHourlyRateForProfession(
  professionKey: string | undefined,
  language: string
): number {
  const config = getCurrencyConfig(language);
  const trade = professionKey ? PROFESSION_HOURLY_RATES[professionKey] : undefined;
  if (trade) {
    const rate = trade[config.code as keyof TradeRates];
    if (typeof rate === 'number') return rate;
  }
  return getDefaultHourlyRate(language);
}
